import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Empty, Spinner } from "@/components/UIKit";
import { fmtDateTime, fmtDate } from "@/lib/format";
import { Send, Loader2, Copy, X, Mail } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Invitasjoner() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => setItems((await api.get("/invitations")).data);
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try { await api.post(`/invitations/cancel/${id}`); toast.success("Invitasjon avlyst"); load(); }
    catch { toast.error("Feilet"); }
  };

  return (
    <div data-testid="invitasjoner-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-heading font-medium text-zinc-100">Portal-invitasjoner</div>
          <div className="text-xs text-zinc-500 mt-0.5">Send engangslenke via e-post til bedriftskunder</div>
        </div>
        <button onClick={() => setOpen(true)}
          className="h-9 px-3.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-2"
          data-testid="send-invitation-btn">
          <Send className="h-4 w-4" /> Send invitasjon
        </button>
      </div>

      <Card className="overflow-hidden">
        {!items ? <div className="p-6"><Spinner /></div>
          : items.length === 0 ? <Empty title="Ingen invitasjoner sendt ennå" />
          : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
                <th className="py-2 px-4 font-medium">E-post</th>
                <th className="py-2 px-4 font-medium">Navn</th>
                <th className="py-2 px-4 font-medium">Sendt</th>
                <th className="py-2 px-4 font-medium">Utløper</th>
                <th className="py-2 px-4 font-medium">Status</th>
                <th className="py-2 px-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-zinc-900">
                  <td className="py-2 px-4 text-zinc-100">{i.email}</td>
                  <td className="py-2 px-4 text-zinc-300">{i.full_name}</td>
                  <td className="py-2 px-4 text-zinc-500 text-xs">{fmtDateTime(i.created_at)}</td>
                  <td className="py-2 px-4 text-zinc-500 text-xs">{fmtDate(i.expires_at)}</td>
                  <td className="py-2 px-4">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      i.status === "pending" ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                      : i.status === "accepted" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      : "bg-zinc-800 text-zinc-500 border-zinc-700"
                    }`}>
                      {i.status === "pending" ? "Venter" : i.status === "accepted" ? "Godkjent" : i.status === "expired" ? "Utløpt" : "Avlyst"}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    {i.status === "pending" && (
                      <button onClick={() => cancel(i.id)} className="text-xs text-red-400 hover:underline">Avlys</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <NyInvitasjon open={open} onOpenChange={setOpen} onSent={() => { setOpen(false); load(); }} />
    </div>
  );
}

function NyInvitasjon({ open, onOpenChange, onSent }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "", email: "", full_name: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null); setForm({ customer_id: "", email: "", full_name: "" });
      api.get("/customers?kind=bedrift&limit=200").then(({ data }) => setCustomers(data.items || []));
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/invitations/send", form);
      setResult(data);
      if (data.email_status === "sent") toast.success("Invitasjon sendt på e-post");
      else if (data.email_status === "failed") toast.warning("Lenke opprettet, men e-post kunne ikke sendes — kopier lenken under");
      else toast.info("Lenke opprettet — Resend er ikke konfigurert, kopier lenken under");
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!result?.activation_url) return;
    navigator.clipboard.writeText(result.activation_url);
    toast.success("Lenke kopiert");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Send portal-invitasjon</DialogTitle>
          <DialogDescription className="sr-only">Send engangslenke til bedriftskunde</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3">
            <div className="text-sm text-emerald-400 flex items-center gap-2"><Mail className="h-4 w-4" /> Invitasjonen er opprettet</div>
            <div className="text-xs text-zinc-500">Aktiveringslenke (gyldig i 7 dager, kan brukes én gang):</div>
            <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-md p-2">
              <code className="flex-1 text-xs text-zinc-300 break-all select-all font-mono">{result.activation_url}</code>
              <button onClick={copyLink} className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400" data-testid="copy-link"><Copy className="h-3.5 w-3.5" /></button>
            </div>
            {result.email_status === "sent" && <div className="text-xs text-emerald-400">✓ E-post sendt via Resend</div>}
            {result.email_status !== "sent" && <div className="text-xs text-amber-400">⚠ E-post ikke sendt: {typeof result.email_detail === "string" ? result.email_detail : "Resend ikke aktivert"}. Send lenken manuelt.</div>}
            <button onClick={() => { setResult(null); }} className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium">Send en til</button>
            <button onClick={onSent} className="w-full h-10 rounded-md border border-zinc-800 text-sm">Ferdig</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1">Bedriftskunde</label>
              <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid="inv-customer">
                <option value="">— velg bedrift —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_number} · {c.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1">Kontaktpersonens navn</label>
              <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid="inv-name" />
            </div>
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1">E-post</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid="inv-email" />
            </div>
            <button type="submit" disabled={busy} className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2" data-testid="inv-send">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} <Send className="h-4 w-4" /> Send invitasjon
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
